"use client";

import { useCallback, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { useCheckoutRegistration } from "src/hooks/useCheckoutRegistration";
import { useOrder } from "src/contexts";
import { orderQueryKey } from "src/hooks/useOrder";
import { useCartDrawer } from "src/contexts/cart-drawer-context";
import { orderApi } from "src/services/api";

/**
 * Put one work in the cart and show the cart.
 *
 * Shared by the artwork page and the gallery's quick-add sheet, so both make
 * the same call, refresh the same cache and register the same way out to
 * checkout. Call it once per page, from a component that stays mounted: the
 * checkout registration lives as long as the hook does, and a sheet that
 * closed after adding would otherwise take the cart drawer's checkout button
 * with it.
 */
export function useAddArtworkToCart(fallbackError: string) {
  const { ensureOrderExists } = useOrder();
  const { openCartDrawer } = useCartDrawer();
  const queryClient = useQueryClient();
  // Captured after a successful add so the cart drawer can offer checkout.
  // Without this the drawer's only button is "continue ordering", which just
  // closes it — the piece is in the cart and unreachable.
  const [checkoutTarget, setCheckoutTarget] = useState<
    { orderId: string; guestToken: string } | null
  >(null);
  useCheckoutRegistration(checkoutTarget);

  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Resolves true once the line is in the cart and the drawer is open. */
  const add = useCallback(
    async (slug: string, variantId: string): Promise<boolean> => {
      setError(null);
      setAdding(true);
      try {
        const { orderId, guestToken } = await ensureOrderExists();
        // Slug and size only. The server resolves which file gets printed and
        // re-checks that this size is allowed for this master.
        await orderApi.orderControllerAddArtwork(
          orderId,
          { slug, variantId, quantity: 1 },
          guestToken,
        );

        // Refresh the cart before showing it. The drawer renders whatever is
        // in the order query cache, and adding a line server-side does not
        // touch that cache — so opening straight away showed an empty cart
        // whenever the order had not already been fetched on this page.
        await queryClient.invalidateQueries({ queryKey: orderQueryKey(orderId) });

        setCheckoutTarget({ orderId, guestToken });
        openCartDrawer();
        return true;
      } catch (err: unknown) {
        Sentry.captureException(err);
        const data = (err as { response?: { data?: { message?: unknown } } })
          ?.response?.data?.message;
        const message = Array.isArray(data)
          ? data.filter(Boolean).join(", ")
          : typeof data === "string" && data
            ? data
            : null;
        setError(message || fallbackError);
        return false;
      } finally {
        setAdding(false);
      }
    },
    [ensureOrderExists, openCartDrawer, queryClient, fallbackError],
  );

  const clearError = useCallback(() => setError(null), []);

  return { add, adding, error, clearError };
}
